import { useOverlay } from '@aigc/overlay-hook';
import {
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Inbox, NovuProvider } from '@novu/react';
import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  notFound,
  Outlet,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Button, Dropdown, Layout, Result, Space, type MenuProps } from 'antd';
import { isHTTPError, isKyError } from 'ky';
import { useEffect, useState } from 'react';
import { Sidebar } from '../../components/side-bar';
import { AnnouncementListButton } from '../../features/announcements/components/announcement-list-button';
import { AnnouncementModal } from '../../features/announcements/components/announcement-modal';
import { logout, redirectToLoginPage } from '../../features/auth/api';
import { clearAccessToken } from '../../features/auth/utils';
import { initChatSdk } from '../../features/chat-sdk/init';
import { canAccessRouteMatches } from '../../features/common/route-permission';
import { imageCredis, videoCredis } from '../../features/credit/api';
import { PriceListButton } from '../../features/credit/components/price-list-button';
import { queryMe } from '../../features/me/api';
import { ChangePasswordModal } from '../../features/me/components/change-password-modal';
import { UsageButton } from '../../features/me/components/usage-button';
import { RunningTaskListButton } from '../../features/task/components/running-task-list';
import { queryClient } from '../../query-client';

const isAuthError = (error: unknown) =>
  isKyError(error) && isHTTPError(error) && error.response.status === 401;

const CustomErrorComponent = ({ error }: ErrorComponentProps) => {
  useEffect(() => {
    if (isAuthError(error)) {
      return;
    }
    captureException(error);
  }, [error]);

  if (isAuthError(error)) {
    clearAccessToken();
    redirectToLoginPage();
    return null;
  }

  return (
    <Result
      status="error"
      title="无法获取用户信息，请刷新重试"
      subTitle={error instanceof Error ? error.message : String(error)}
      extra={<Button onClick={() => window.location.reload()}>刷新页面</Button>}
    ></Result>
  );
};

export const Route = createFileRoute('/_creator-center')({
  component: CreatorCenter,
  beforeLoad: async ({ matches }) => {
    queryClient.prefetchQuery({
      ...videoCredis,
      staleTime: 'static',
      gcTime: Infinity,
    });
    queryClient.prefetchQuery({
      ...imageCredis,
      staleTime: 'static',
      gcTime: Infinity,
    });
    const me_data = await queryClient.fetchQuery(queryMe);
    const set = new Set(
      me_data.permissions.map(([obj, act]) => `${obj}:${act}`),
    );

    if (!canAccessRouteMatches(matches, set)) {
      throw notFound();
    }

    // 登录授权成功，注入客服 Chat SDK（幂等）
    initChatSdk({ userId: me_data.id, name: me_data.name });

    return {
      me: me_data,
      username: me_data.name,
      permissions: set,
    };
  },
  errorComponent: CustomErrorComponent,
});

function CreatorCenter() {
  const [openOverlay] = useOverlay();
  const [collapsed, setCollapsed] = useState(false);

  const user_menu: MenuProps['items'] = [
    {
      key: 'my_account',
      icon: <UserOutlined />,
      label: '我的账户',
    },
    {
      key: 'change_password',
      icon: <KeyOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handle_user_menu_click: MenuProps['onClick'] = async ({ key }) => {
    switch (key) {
      case 'my_account': {
        const popupWidth = 1280;
        const popupHeight = 800;
        const popupLeft = Math.max(
          0,
          Math.round((window.screen.availWidth - popupWidth) / 2),
        );
        const popupTop = Math.max(
          0,
          Math.round((window.screen.availHeight - popupHeight) / 2),
        );

        window.open(
          new URL('/account', import.meta.env.NX_PUBLIC_AUTH_BASE_URL),
          '_blank',
          `popup,width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},noopener,noreferrer`,
        );
        break;
      }
      case 'logout':
        try {
          await logout();
        } catch (error) {
          console.error(error);
        }

        clearAccessToken();
        redirectToLoginPage();
        break;
      case 'change_password':
        openOverlay(<ChangePasswordModal></ChangePasswordModal>);
        break;
    }
  };

  const { data } = useQuery(queryMe);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Layout className="min-h-0 flex-auto">
        <Layout.Sider
          theme="light"
          breakpoint="lg"
          collapsed={collapsed}
          collapsedWidth={0}
          width={256}
          trigger={null}
          onCollapse={(value, type) => {
            if (type === 'responsive') {
              setCollapsed(value);
            }
          }}
        >
          <Sidebar />
        </Layout.Sider>
        <Layout className="min-h-0 min-w-0">
          <Layout.Header className="w-screen border-b border-solid border-primary-200 bg-white px-4 lg:w-full">
            <div className="flex h-full items-center">
              <Button
                className="lg:hidden"
                variant="filled"
                color="default"
                icon={
                  collapsed ? (
                    <MenuUnfoldOutlined></MenuUnfoldOutlined>
                  ) : (
                    <MenuFoldOutlined></MenuFoldOutlined>
                  )
                }
                onClick={() => {
                  setCollapsed((value) => !value);
                }}
              ></Button>
              <div className="flex-auto"></div>
              <Space size="middle">
                <PriceListButton></PriceListButton>
                <UsageButton></UsageButton>
                <RunningTaskListButton></RunningTaskListButton>
                <NovuProvider
                  applicationIdentifier={import.meta.env.NX_PUBLIC_NOVU_APP_ID}
                  subscriberId="announcements"
                  apiUrl="https://api.notify.sendercenter.com"
                  socketUrl="https://socket.notify.sendercenter.com"
                >
                  <AnnouncementModal />
                  <AnnouncementListButton />
                </NovuProvider>
                <Inbox
                  applicationIdentifier={import.meta.env.NX_PUBLIC_NOVU_APP_ID}
                  subscriberId={data.name}
                  backendUrl="https://api.notify.sendercenter.com"
                  socketUrl="https://socket.notify.sendercenter.com"
                  localization={{
                    'inbox.filters.dropdownOptions.unread': '未读',
                    'inbox.filters.dropdownOptions.default': '全部',
                    'inbox.filters.dropdownOptions.archived': '归档',
                    'inbox.filters.dropdownOptions.snoozed': '稍后提醒',
                    'inbox.filters.labels.unread': '未读',
                    'inbox.filters.labels.default': '收件箱',
                    'inbox.filters.labels.archived': '已归档',
                    'inbox.filters.labels.snoozed': '稍后提醒',

                    'notifications.emptyNotice': '暂无消息，稍后再来',
                    'notifications.actions.readAll': '全部已读',
                    'notifications.actions.archiveAll': '全部归档',
                    'notifications.actions.archiveRead': '归档已读',
                    'notifications.newNotifications': (ctx) =>
                      `收到 ${ctx.notificationCount} 条新通知`,
                    'notification.snoozedUntil': '稍后提醒',

                    'notification.actions.read.tooltip': '标记为已读',
                    'notification.actions.unread.tooltip': '标记为未读',
                    'notification.actions.archive.tooltip': '归档',
                    'notification.actions.unarchive.tooltip': '撤销归档',
                    'notification.actions.snooze.tooltip': '稍后提醒',
                    'notification.actions.unsnooze.tooltip': '撤销稍后提醒',

                    'snooze.options.anHourFromNow': '一小时后',
                    'snooze.options.inOneDay': '一天后',
                    'snooze.options.inOneWeek': '一周后',
                    'snooze.options.customTime': '自定义时间',
                    'snooze.datePicker.apply': '应用',
                    'snooze.datePicker.cancel': '取消',
                    'snooze.datePicker.pastDateTooltip':
                      '选择的时间至少大于 3 分钟',
                    'snooze.datePicker.exceedingLimitTooltip': ({ days }) =>
                      `选择的时间不能够超过 ${days === 1 ? '24 小时' : `${days} 天`}`,
                    'snooze.datePicker.timePickerLabel': '时间',
                    'snooze.datePicker.noDateSelectedTooltip': '请选择日期',

                    'preferences.title': '偏好设置',
                    'preferences.global': '全局配置',
                    'preferences.schedule.info':
                      '关键通知和应用内通知仍会在你设定的时间之外送达。',
                    'preferences.schedule.monday': '周一',
                    'preferences.schedule.tuesday': '周二',
                    'preferences.schedule.wednesday': '周三',
                    'preferences.schedule.thursday': '周四',
                    'preferences.schedule.friday': '周五',
                    'preferences.schedule.saturday': '周六',
                    'preferences.schedule.sunday': '周天',
                    'preferences.schedule.days': '周几',
                    'preferences.schedule.from': '开始时间',
                    'preferences.schedule.to': '结束时间',
                    'preferences.schedule.description': '允许在以下时段通知',
                    'preferences.schedule.title': '时间表',
                    'preferences.schedule.headerInfo':
                      '设置你的时间表。设定的时间之外的外部渠道通知将会暂停。应用内和关键通知仍会收到。',
                    'preferences.schedule.dayScheduleCopy.title': '复制时间到',
                    'preferences.schedule.dayScheduleCopy.selectAll': '全选',
                    'preferences.schedule.dayScheduleCopy.apply': '应用',
                    'preferences.schedule.copyTimesTo': '复制时间到',
                  }}
                />
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: user_menu,
                    onClick: handle_user_menu_click,
                  }}
                >
                  <Button
                    shape="round"
                    variant="text"
                    color="default"
                    icon={<UserOutlined />}
                  >
                    {data.name}
                  </Button>
                </Dropdown>
              </Space>
            </div>
          </Layout.Header>
          <Layout.Content className="min-h-0 w-screen min-w-0 overflow-auto lg:w-full">
            <Outlet />
          </Layout.Content>
        </Layout>
        <TanStackRouterDevtools></TanStackRouterDevtools>
      </Layout>
    </div>
  );
}
